/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { useSetDeviceConfigMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { setIsLoadingStatus, setOpenToast } from "../../../store/helpers/helperReducers";
import { prepareSummaryPayload } from "../helpers";
import { DasModelNames, OPERATIONTYPES } from "../../../globals/constants";
import { IFullDeviceDetails } from "../../../store/api/deviceConfig/types";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { useAddDeviceSiteMutation, useAddDeviceToNotificationGroupMutation, useRemoveDevicesFromNotificationGroupMutation } from "../../../store/api/admin/adminApiSlice";
import { useDeploymentMutation, useGetGroupsQuery, useAddDeviceLabelsMutation } from "../../../store/api/devices/deviceApiSlice";
import { errorMessage } from "../../../utils/helpers";
import { setGroupList } from "../../../store/api/admin/reducers";
import { groupListSelector } from "../../../store/api/admin/selectors";
import { Ioptions } from "../../Shared/Filter/types";
import { prepareOfflineSnmpPayload } from "../../Administrations/AccessControls/Authentication/helpers";

export const useDeviceSummaryData = () => {
  const dispatch = useStoreDispatch();
  const [selectedkey, setSelectedKey] = useState<any>("");
  const [newValue, setNewValue] = useState<any>("");
  const deviceDetail: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const groupList: Ioptions[] = useSelector(groupListSelector) || [];

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();
  const [addDeviceToNotificationGroup, addDevicesToGroupStatus] = useAddDeviceToNotificationGroupMutation();
  const [removeDevicesFromNotificationGroup, removeDevicesFromGroupStatus] = useRemoveDevicesFromNotificationGroupMutation();
  const [deployment] = useDeploymentMutation();
  const [addLabelsToDevice] = useAddDeviceLabelsMutation();
  const groups = useGetGroupsQuery();
  const [addOfflineSite] = useAddDeviceSiteMutation();

  const isDASDevice = DasModelNames.some(
    model => model.value === deviceDetail?.modelName
  );


  useEffect(() => {
    dispatch(
      setIsLoadingStatus({
        [selectedkey]: configStatus.isLoading || addDevicesToGroupStatus.isLoading || removeDevicesFromGroupStatus.isLoading,
      })
    );
  }, [addDevicesToGroupStatus.isLoading, configStatus.isLoading, dispatch, removeDevicesFromGroupStatus.isLoading, selectedkey]);

  useEffect(() => {
    const groupsData = groups.data || [];
    dispatch(setGroupList(groupsData));
  }, [groups.data, dispatch]);

  const handleUpdate = async (item: string, value: any, newVal: any) => {
    try {
      let successMessage: string = "";
      let response: any;
      const operationType = OPERATIONTYPES.UPDATE;

      if (item === "DEPLOYMENT TYPE") {
        const deploymentValue = newVal || value;
        response = await deployment({
          serialNumber: deviceDetail?.serialNumber,
          deployment: deploymentValue,
        });

        successMessage = `Deployment updated to: ${deploymentValue}`;
      } else if (item === "NOTIFICATIONS") {
        if (newVal === "System Default" && !!deviceDetail.groupKey) {
          const group = groupList?.find((item) => item?.value === deviceDetail.groupKey);
          const name = group ? group.text : "";
          response = await removeDevicesFromNotificationGroup({
            groupKey: deviceDetail?.groupKey.toString() || "",
            deviceKeys: [deviceDetail?.objectKey || ""],
          });
          successMessage = `Device removed from Group: ${name!} successfully`;
        } else {
          const group = groupList?.find((item) => item?.value === newVal.toString());
          const name = group ? group.text : "";
          response = await addDeviceToNotificationGroup({
            groupKey: newVal !== "System Default" ? newVal.toString() : "",
            deviceKeys: [deviceDetail?.objectKey || ""],
          });
          successMessage = `Device is added to Group: ${name!} successfully`;
        }
      } else if (item === "LABEL") {
        const labelValue = newVal;
      const labelsArray = labelValue.trim() === "" ? [] : labelValue.split(",").map((label: string) => label.trim()).filter((label: string) => label !== "");
        response = await addLabelsToDevice({
          deviceKey: deviceDetail?.serialNumber ? deviceDetail.serialNumber : deviceDetail?.serialNumber,
          labelNames: labelsArray,
        });
        if (response.data?.status === 200)
      {
        successMessage = `Labels updated to: ${deviceDetail?.serialNumber}`;
        }
    } else {
        if (isDASDevice && (item === "LATITUDE" || item === "LONGITUDE")) {
          const updatedDeviceDetail = {
            ...deviceDetail,
            latitude: item === "LATITUDE" ? newVal : deviceDetail.latitude,
            longitude: item === "LONGITUDE" ? newVal : deviceDetail.longitude,
          };
          const offlinePayload = prepareOfflineSnmpPayload(updatedDeviceDetail);
          response = await addOfflineSite(offlinePayload);
          successMessage = `${item} updated to: ${newVal}`;
        }
        else {
          const summaryPayload = prepareSummaryPayload(item, value, newVal, deviceDetail?.objectKey, operationType);
          response = await setDeviceConfig(summaryPayload);
          successMessage = `The data update request has been submitted`;
        }

      }

      if (response?.error || configStatus?.isError) throw response?.error;

      setNewValue("");
      setSelectedKey("");
      dispatch(
        setOpenToast({
          openToast: true,
          toastType: "success",
          message: successMessage,
        })
      );
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };


  return {
    selectedkey,
    setSelectedKey,
    newValue,
    setNewValue,
    handleUpdate,
    configStatus,
    groupList,
    deviceDetail
  };
};